import { useFacet, useFacets, useActions, useAppStateSelector, pick } from "@nosto/preact"
import { useMemo } from "preact/hooks"

import { Swiper, SwiperSlide } from "./Swiper"

import { getSrc } from "../helpers"
import { isVisualFilter } from "../helpers"

const IMG_SWIPER_OPTIONS = {
    watchOverflow: true,
    loop: false,
    slidesPerView: 3,
    slidesPerGroup: 1,
    slidesOffsetBefore: 8,
    slidesOffsetAfter: 8,
    spaceBetween: 8,
    breakpoints: {
        768: {
            slidesPerView: 4,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            spaceBetween: 16,
        },
        990: {
            slidesPerView: 5,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            spaceBetween: 16,
        },
        1440: {
            slidesPerView: 6,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            spaceBetween: 16,
        }
    }
};

const NOIMG_SWIPER_OPTIONS = {
    watchOverflow: true,
    loop: false,
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
    spaceBetween: 8,
    breakpoints: {
        768: {
            spaceBetween: 16,
        }
    }
};

function VisualFilterContent({ item }) {
    return (
        <>
            {item.image && (
                <div class="ns-vfilter-img-wrap">
                    <img
                        src={getSrc(item.image, 680)}
                        alt={item.label || item.collection_filter?.title || ''} 
                    />
                </div>
            )}
            <span class="ns-folio-reg">
                {item.label || item.collection_filter?.title || ''}
            </span>
        </>
    )
}

function VisualFilterWrapper({ facet, options }) {
    const { query } = useAppStateSelector(state => pick(state, "query"));
    const { updateSearch } = useActions();
    const { selectedFiltersCount, toggleProductFilter } = useFacet(facet);
    const facetLabel = useMemo(() => `ns-visual-filters-${facet?.id}`,[]);
    
    // only 1 visual filter can be toggled at a time
    const toggleVisualFilter = (newVisualfilter) => {
        const curVisualFilters = query?.products?.filter?.find(f => f.field === facet.field);
        if(curVisualFilters && curVisualFilters.value?.[0] !== newVisualfilter.value) {
            const otherFilters = query?.products?.filter?.filter(f => f.field !== facet.field);
            updateSearch({ products: { filter: [ ...otherFilters, { ...curVisualFilters, value: [ newVisualfilter.value ] } ] }})
        } else {
            toggleProductFilter(facet.field, newVisualfilter.value, !newVisualfilter.selected)
        }
    }

    return (
        <>
            <div class="ns-facet-header sr-only visually-hidden">
                <h3 id={`filters-heading-${facetLabel}`}>
                    Sub collections {selectedFiltersCount > 0 ? `(${selectedFiltersCount})` : ''}
                </h3>
            </div>
            <Swiper
                className="ns-vfilter-swiper"
                options={options.withImages ? IMG_SWIPER_OPTIONS : NOIMG_SWIPER_OPTIONS} 
                showProgressBar={options.withImages}
                gutterTotal={(window.innerWidth < 768 && options.withImages) ? 16 : 32}
                init
            >
                {window.NOSTO_VISUAL_FILTERS.map(item => {
                    const filter = facet?.data?.find(f => f.value === item.collection_filter?.id);
                    return (
                        <SwiperSlide
                            key={item.label ?? item.url ?? item.image ?? item.collection_filter.id}
                            className={`${filter?.selected ? 'ns-selected' : ''}`}
                            data-nosto-element="facet-setting"
                        >
                            {options.areFilters ? (
                                <label
                                    className={`ns-vfilter ns-relative ns-flex ns-flex-col ns-items-center ns-justify-center ${filter ? '' : 'ns-vfilter-disabled'}`}
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (filter && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault();
                                            toggleVisualFilter(filter);
                                        }
                                    }}
                                >
                                    <input
                                        class="sr-only visually-hidden"
                                        type="checkbox"
                                        name={`ns-vfiler-${facet.name.toLowerCase().replaceAll(' ', '-')}`}
                                        tabIndex={-1}
                                        checked={filter ? filter.selected : false}
                                        onChange={e => {
                                            e.preventDefault()
                                            if(!filter) return;
                                            toggleVisualFilter(filter)
                                        }}
                                    />
                                    <VisualFilterContent item={item} />
                                </label>
                            ) : (
                                <a href={item.url} class="ns-vfilter ns-flex ns-flex-col ns-items-center ns-justify-center">
                                    <VisualFilterContent item={item} />
                                </a>
                            )}
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        </>
    )
}

export default function VisualFilter() {
    const { facets } = useFacets();
    const facet = useMemo(() => facets.find(f => isVisualFilter(f.name)), [ isVisualFilter, facets ]);
    const hasVisualFilters = useMemo(() => window.NOSTO_VISUAL_FILTERS?.length > 0, []);
    const options = useMemo(() => {
        return hasVisualFilters ? {
            areFilters: window.NOSTO_VISUAL_FILTERS.every(item => !!item.collection_filter),
            withImages: window.NOSTO_VISUAL_FILTERS.every(item => !!item.image),
            count: window.NOSTO_VISUAL_FILTERS.length
        } : null
    },[])

    if(!hasVisualFilters || (options.areFilters && !facet)) return null;

    return (
        <div class={`ns-visual-filters ${options.withImages ? 'ns-image-filters' : 'ns-noimage-filters'}`}>
            <VisualFilterWrapper 
                facet={facet} 
                options={options}
            />
        </div>
    )
}